/**
 * File: generationSessionStore.ts
 * Description: localStorage-backed record of an in-progress architecture
 *   generation run. Persisting the run lets the Generation page auto-resume
 *   the section sequence after a full page reload, instead of stopping and
 *   forcing the user to click "Start Generation" again. All access is wrapped
 *   so a missing / over-quota / private-mode storage degrades silently.
 */

import { ArchitectureSection } from '../types/generation';

/** Persisted description of an active (or interrupted) generation run. */
export interface GenerationSession {
  /** Owning project id. */
  projectId: number;
  /** Sections the run is generating, in order. */
  sections: ArchitectureSection[];
  /** True when the run regenerates every section (vs. resuming remaining). */
  regenerateAll: boolean;
}

/** Prefix for the per-project storage key. */
const STORAGE_KEY_PREFIX = 'archie.generationSession.v1';

/** Build the storage key for a given project. */
const keyFor = (projectId: number): string =>
  `${STORAGE_KEY_PREFIX}.${projectId}`;

/**
 * Persist the active generation session for a project. Overwrites any existing
 * session for the same project.
 *
 * @param session - The generation session to persist.
 */
export const saveGenerationSession = (session: GenerationSession): void => {
  try {
    localStorage.setItem(keyFor(session.projectId), JSON.stringify(session));
  } catch {
    // Storage may be unavailable (private mode / quota). Degrade silently —
    // generation still works, it just will not auto-resume after a reload.
  }
};

/**
 * Load the persisted generation session for a project.
 *
 * @param projectId - Owning project id.
 * @returns The stored session, or null when absent or corrupt.
 */
export const loadGenerationSession = (
  projectId: number,
): GenerationSession | null => {
  try {
    const raw = localStorage.getItem(keyFor(projectId));
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    const session = parsed as GenerationSession;
    if (!Array.isArray(session.sections)) {
      return null;
    }
    return session;
  } catch {
    return null;
  }
};

/**
 * Remove the persisted generation session for a project (no-op when absent).
 *
 * @param projectId - Owning project id.
 */
export const clearGenerationSession = (projectId: number): void => {
  try {
    localStorage.removeItem(keyFor(projectId));
  } catch {
    // Degrade silently — see saveGenerationSession.
  }
};
